import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import Header from "./Header";
import Footer from "./Footer";
import FaqSection from "components/PublicPagesReusable/FaqSection";
import "assets/css/PteScoreAcademic.css";

/* ----------------------------------------------------------------
   Static data — official Pearson PTE Academic -> IELTS alignment.
   rows: [band, min, max]
   ---------------------------------------------------------------- */
const ACAD = {
  Overall: [[4.5, 24, 30], [5.0, 31, 38], [5.5, 39, 46], [6.0, 47, 54], [6.5, 55, 62], [7.0, 63, 70], [7.5, 71, 78], [8.0, 79, 85], [8.5, 86, 89], [9.0, 90, 90]],
  Listening: [[4.5, 26, 32], [5.0, 33, 39], [5.5, 40, 46], [6.0, 47, 52], [6.5, 53, 57], [7.0, 58, 63], [7.5, 64, 68], [8.0, 69, 74], [8.5, 75, 80], [9.0, 81, 90]],
  Reading: [[4.5, 29, 35], [5.0, 36, 41], [5.5, 42, 47], [6.0, 48, 53], [6.5, 54, 58], [7.0, 59, 64], [7.5, 65, 69], [8.0, 70, 74], [8.5, 75, 80], [9.0, 81, 90]],
  Speaking: [[4.5, 14, 23], [5.0, 24, 38], [5.5, 39, 53], [6.0, 54, 65], [6.5, 66, 75], [7.0, 76, 82], [7.5, 83, 87], [8.0, 88, 89], [8.5, 90, 90]],
  Writing: [[4.5, 17, 28], [5.0, 29, 40], [5.5, 41, 50], [6.0, 51, 59], [6.5, 60, 68], [7.0, 69, 76], [7.5, 77, 84], [8.0, 85, 89], [8.5, 90, 90]],
};

const CEFR = [[85, 90, "C2"], [76, 84, "C1"], [59, 75, "B2"], [43, 58, "B1"], [30, 42, "A2"], [10, 29, "A1"]];

/* PR bands = IELTS 6/7/8 in EVERY skill -> per-module minimums from the alignment */
const PR = {
  superior: { pts: 20, name: "Superior", req: { Listening: 69, Reading: 70, Speaking: 88, Writing: 85 } },
  proficient: { pts: 10, name: "Proficient", req: { Listening: 58, Reading: 59, Speaking: 76, Writing: 69 } },
  competent: { pts: 0, name: "Competent", req: { Listening: 47, Reading: 48, Speaking: 54, Writing: 51 } },
};

const SK = ["Listening", "Reading", "Speaking", "Writing"];
const MARKER_ORDER = ["Listening", "Reading", "Speaking", "Writing", "Overall"];
const AB = { Listening: "L", Reading: "R", Speaking: "S", Writing: "W", Overall: "O" };

const b1 = (b) => Number(b).toFixed(1);

const bandOf = (skill, score) => {
  const r = ACAD[skill];
  const mx = r[r.length - 1][2];
  for (let i = 0; i < r.length; i++) {
    if (score >= r[i][1] && score <= r[i][2]) return r[i][0];
  }
  return score > mx ? r[r.length - 1][0] : null;
};

const cefrOf = (s) => {
  for (let i = 0; i < CEFR.length; i++) {
    if (s >= CEFR[i][0] && s <= CEFR[i][1]) return CEFR[i][2];
  }
  return null;
};

const pct = (band) => Math.max(0, Math.min(1, (band - 4.5) / 4.5));
const trackLeft = (p) => `calc(12px + (100% - 24px) * ${p})`;

/* Per-row IELTS display state derived from a raw input value. */
const rowDisplay = (skill, raw) => {
  const value = (raw || "").trim();
  if (value === "") return { bad: false, text: "—", vClass: "v dim", cefr: null };
  const n = parseFloat(value);
  if (Number.isNaN(n) || n < 10 || n > 90) {
    return { bad: true, text: "—", vClass: "v dim", cefr: null };
  }
  const b = bandOf(skill, n);
  return {
    bad: false,
    text: b === null ? "< 4.5" : b1(b),
    vClass: "v" + (b !== null && b >= 7 ? " good" : ""),
    cefr: skill === "Overall" ? cefrOf(n) : null,
  };
};

/* Replicates placeMarkers(): groups overlapping bands and spreads them. */
const computeMarkers = (bands) => {
  const groups = {};
  MARKER_ORDER.forEach((s) => {
    const b = bands[s];
    if (b === undefined) return;
    const key = b === null ? "lo" : String(b);
    (groups[key] = groups[key] || []).push(s);
  });
  const info = {};
  Object.keys(groups).forEach((key) => {
    const list = groups[key];
    const n = list.length;
    list.forEach((s, i) => {
      const b = bands[s];
      const p = b === null ? 0 : pct(b);
      info[s] = {
        show: true,
        left: p,
        ox: (i - (n - 1) / 2) * 11,
        good: s !== "Overall" && b !== null && b >= 7,
      };
    });
  });
  return info;
};

/* Build IELTS scale ticks (4.5 -> 9.0). */
const SCALE_TICKS = (() => {
  const ticks = [];
  for (let i = 0; i <= 9; i++) {
    const b = 4.5 + 0.5 * i;
    const em = b === 6 || b === 7 || b === 8;
    const keep = b === 4.5 || b === 9;
    ticks.push({
      b,
      em,
      keep,
      left: trackLeft(pct(b)),
      label: b % 1 === 0 ? b + ".0" : String(b),
    });
  }
  return ticks;
})();

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "EducationalOrganization", "@id": "https://www.languageacademy.com.au/#organization", name: "Language Academy", url: "https://www.languageacademy.com.au/", logo: { "@type": "ImageObject", url: "https://www.languageacademy.com.au/assets/la-logo.png" }, description: "PTE, NAATI CCL and IELTS coaching with 50,000+ students enrolled, a 95% success rate and Australia's most accurate AI practice platform.", telephone: "+61426230061", email: "info@languageacademy.com.au", address: { "@type": "PostalAddress", streetAddress: "46 George Street", addressLocality: "Parramatta", addressRegion: "NSW", addressCountry: "AU" } },
    { "@type": "WebApplication", "@id": "https://www.languageacademy.com.au/pte-score-calculator/#app", name: "PTE Academic Score Calculator", url: "https://www.languageacademy.com.au/pte-score-calculator/", applicationCategory: "EducationApplication", operatingSystem: "All (web browser)", browserRequirements: "Requires JavaScript.", description: "Free PTE Academic score calculator. Type your module and overall scores to see the IELTS band for every skill, your CEFR level and your Australian PR English points.", inLanguage: "en-AU", isAccessibleForFree: true, offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" }, featureList: ["Per-module PTE to IELTS conversion", "Overall PTE to IELTS and CEFR", "Australian PR English points by module", "What you need for the next PR band"], creator: { "@id": "https://www.languageacademy.com.au/#organization" } },
    { "@type": "FAQPage", "@id": "https://www.languageacademy.com.au/pte-score-calculator/#faq", mainEntity: [
      { "@type": "Question", name: "How do I use this PTE Academic score calculator?", acceptedAnswer: { "@type": "Answer", text: "Type your four module scores (Listening, Reading, Speaking, Writing) from your PTE score report - each one instantly shows its IELTS band. Add your overall score to see its IELTS band and CEFR level, and your Australian PR English points appear below." } },
      { "@type": "Question", name: "How is the overall PTE score calculated?", acceptedAnswer: { "@type": "Answer", text: "Pearson's scoring engine assesses your whole test, including integrated tasks that count towards more than one skill - so the overall is not a simple average of your four modules." } },
      { "@type": "Question", name: "How do I convert my PTE score to IELTS?", acceptedAnswer: { "@type": "Answer", text: "Use the official Pearson alignment, and note each skill converts on its own scale. As an overall guide, PTE 79-85 = IELTS 8.0, PTE 71-78 = IELTS 7.5, PTE 63-70 = IELTS 7.0." } },
      { "@type": "Question", name: "What PTE score do I need for Superior English (20 PR points)?", acceptedAnswer: { "@type": "Answer", text: "Superior English means IELTS 8 in each skill. On PTE Academic that is Listening 69, Reading 70, Speaking 88 and Writing 85 - not a flat 79." } },
      { "@type": "Question", name: "What PTE score do I need for Proficient English (10 PR points)?", acceptedAnswer: { "@type": "Answer", text: "Proficient English means IELTS 7 in each skill: Listening 58, Reading 59, Speaking 76 and Writing 69 on PTE Academic." } },
      { "@type": "Question", name: "What is a good PTE score?", acceptedAnswer: { "@type": "Answer", text: "PTE 65+ overall (about IELTS 7.0) meets most universities and is broadly Proficient-level. 90 is the highest possible score." } },
      { "@type": "Question", name: "Can I use my PTE score for Canada PR?", acceptedAnswer: { "@type": "Answer", text: "For Canada you need PTE Core (not PTE Academic), which maps to CLB levels for Express Entry." } },
      { "@type": "Question", name: "How long is a PTE Academic score valid?", acceptedAnswer: { "@type": "Answer", text: "PTE Academic scores are valid for two years from the test date. Results are usually available within about 48 hours." } },
      { "@type": "Question", name: "How do I check my real level before the exam?", acceptedAnswer: { "@type": "Answer", text: "Sit a free scored mock test on the Language Academy AI portal for instant AI scoring and module-by-module feedback." } },
    ] },
  ],
};

const PteScoreAcademic = () => {
  const [scores, setScores] = useState({
    Listening: "",
    Reading: "",
    Speaking: "",
    Writing: "",
    Overall: "",
  });
  const [coachMounted, setCoachMounted] = useState(false);
  const [coachShown, setCoachShown] = useState(false);

  const listeningRef = useRef(null);
  const coachDoneRef = useRef(false);

  /* ---------- derived calculator state ---------- */
  const {
    bands,
    scoreNums,
    count,
    moduleRows,
    overallRow,
  } = useMemo(() => {
    const _bands = {};
    const _scoreNums = {};
    const _moduleRows = {};
    SK.forEach((s) => {
      const disp = rowDisplay(s, scores[s]);
      _moduleRows[s] = disp;
      const value = (scores[s] || "").trim();
      if (value === "") return;
      const n = parseFloat(value);
      if (Number.isNaN(n) || n < 10 || n > 90) return;
      _scoreNums[s] = n;
      _bands[s] = bandOf(s, n);
    });

    const _overallRow = rowDisplay("Overall", scores.Overall);
    const oValue = (scores.Overall || "").trim();
    if (oValue !== "") {
      const on = parseFloat(oValue);
      if (!Number.isNaN(on) && on >= 10 && on <= 90) {
        _bands.Overall = bandOf("Overall", on);
      }
    }

    return {
      bands: _bands,
      scoreNums: _scoreNums,
      count: Object.keys(_scoreNums).length,
      moduleRows: _moduleRows,
      overallRow: _overallRow,
    };
  }, [scores]);

  const markers = useMemo(() => computeMarkers(bands), [bands]);

  /* ---------- PR (Australian English points) ---------- */
  const pr = useMemo(() => {
    if (count < 4) return { ready: false };
    const meets = (o) => SK.every((s) => scoreNums[s] >= o.req[s]);
    let lvl = null;
    if (meets(PR.superior)) lvl = "superior";
    else if (meets(PR.proficient)) lvl = "proficient";
    else if (meets(PR.competent)) lvl = "competent";

    if (lvl === "superior") {
      return { ready: true, segOn: lvl, showWin: true };
    }

    const target = lvl === null ? PR.competent : lvl === "competent" ? PR.proficient : PR.superior;
    const now = lvl === null
      ? "<strong>below Competent</strong>"
      : `at <strong>${PR[lvl].name} — ${PR[lvl].pts} point${PR[lvl].pts === 1 ? "" : "s"}</strong>`;
    const msgHtml = `You're ${now}. For <strong>${target.name} (${target.pts} pts)</strong> you need:`;
    const needs = SK.map((s) => {
      const have = scoreNums[s];
      const req = target.req[s];
      return have >= req
        ? { s, ok: true, have }
        : { s, ok: false, have, req, gap: req - have };
    });
    return { ready: true, segOn: lvl, showWin: false, msgHtml, needs };
  }, [count, scoreNums]);

  /* ---------- handlers ---------- */
  const handleChange = useCallback((skill, value) => {
    setScores((prev) => ({ ...prev, [skill]: value }));
  }, []);

  const dismissCoach = useCallback((mark) => {
    if (coachDoneRef.current) return;
    coachDoneRef.current = true;
    setCoachShown(false);
    if (mark) {
      try {
        localStorage.setItem("laCoachSeen", "1");
      } catch (e) {
        /* ignore storage errors */
      }
    }
    setTimeout(() => setCoachMounted(false), 350);
  }, []);

  const handleReset = useCallback(() => {
    setScores({ Listening: "", Reading: "", Speaking: "", Writing: "", Overall: "" });
    if (listeningRef.current) listeningRef.current.focus();
  }, []);

  const handleSample = useCallback(() => {
    setScores({ Listening: "65", Reading: "65", Speaking: "70", Writing: "70", Overall: "66" });
    dismissCoach(true);
  }, [dismissCoach]);

  /* ---------- first-visit coach tooltip ---------- */
  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem("laCoachSeen") === "1";
    } catch (e) {
      /* ignore storage errors */
    }
    if (seen) return undefined;
    setCoachMounted(true);
    const t1 = setTimeout(() => {
      if (!coachDoneRef.current) setCoachShown(true);
    }, 700);
    const t2 = setTimeout(() => dismissCoach(false), 11000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [dismissCoach]);

  /* ---------- reveal-on-scroll ---------- */
  useEffect(() => {
    const els = document.querySelectorAll(".pte-score-academic-page .reveal");
    if (!els.length) return undefined;
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
      );
      els.forEach((el) => io.observe(el));
      return () => io.disconnect();
    }
    els.forEach((el) => el.classList.add("in"));
    return undefined;
  }, []);

  const renderMarker = (s) => {
    const m = markers[s];
    const cls = ["mk", s === "Overall" ? "ov" : "", m && m.show ? "show" : "", m && m.good ? "good" : ""]
      .filter(Boolean)
      .join(" ");
    const style = m
      ? { left: trackLeft(m.left), "--ox": `${m.ox}px` }
      : undefined;
    return (
      <div key={s} className={cls} style={style} aria-hidden="true">
        {AB[s]}
      </div>
    );
  };

  const faqQuestions = [
    {
      question: "How do I use this PTE Academic score calculator?",
      answer:
        "Type your four module scores (Listening, Reading, Speaking, Writing) from your PTE score report — each one instantly shows its IELTS band. Add your overall score to see its IELTS band and CEFR level, and your Australian PR English points appear below.",
    },
    {
      question: "How is the overall PTE score calculated?",
      answer: (
        <>
          Pearson's scoring engine assesses your whole test, including integrated tasks that count towards more than one skill — so the overall is <strong>not a simple average</strong> of your four modules. That's why this calculator asks you to type the overall from your score report instead of estimating it.
        </>
      ),
    },
    {
      question: "How do I convert my PTE score to IELTS?",
      answer:
        "Use the official Pearson alignment — and note each skill converts on its own scale. As an overall guide, PTE 79–85 = IELTS 8.0, PTE 71–78 = IELTS 7.5, PTE 63–70 = IELTS 7.0. The calculator does it per module for you.",
    },
    {
      question: "What PTE score do I need for Superior English (20 PR points)?",
      answer: (
        <>
          Superior English means IELTS 8 in each skill. On PTE Academic that is <strong>Listening 69, Reading 70, Speaking 88 and Writing 85</strong> — not a flat 79. You must reach the score in every module to claim the 20 points.
        </>
      ),
    },
    {
      question: "What PTE score do I need for Proficient English (10 PR points)?",
      answer: (
        <>
          Proficient English means IELTS 7 in each skill: <strong>Listening 58, Reading 59, Speaking 76 and Writing 69</strong> on PTE Academic. Reaching this in all four modules earns 10 points on the skilled-migration points test.
        </>
      ),
    },
    {
      question: "What is a good PTE score?",
      answer:
        "PTE 65+ overall (≈ IELTS 7.0) meets most universities and is broadly Proficient-level. Reaching Superior English (IELTS 8 in each skill) earns the maximum English migration points. 90 is the highest possible score.",
    },
    {
      question: "Can I use my PTE score for Canada PR?",
      answer: (
        <>
          For Canada you need PTE Core (not PTE Academic), which maps to CLB levels for Express Entry. Use our <a href="/pte-score-calculator/core">PTE Core to CLB calculator</a> to convert your scores and check CLB 7 and CLB 9.
        </>
      ),
    },
    {
      question: "How long is a PTE Academic score valid?",
      answer:
        "PTE Academic scores are valid for two years from the test date. Results are usually available within about 48 hours and can be shared securely with institutions and authorities.",
    },
    {
      question: "How do I check my real level before the exam?",
      answer: (
        <>
          Sit a free scored mock test on the Language Academy AI portal. You'll get instant AI scoring on real exam-style questions, module-by-module feedback, and a clear picture of how close you are to your target — <a href="https://www.languageacademy.com.au/mock-test">start free here</a>.
        </>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <html lang="en-AU" />
        <title>PTE Academic Score Calculator | PTE to IELTS & PR Points — Language Academy</title>
        <meta
          name="description"
          content="Free PTE score calculator for PTE Academic — see the IELTS band for every module, your CEFR level and your Australian PR points. Official Pearson alignment."
        />
        <link rel="canonical" href="https://www.languageacademy.com.au/pte-score-calculator/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Language Academy" />
        <meta property="og:title" content="PTE Academic Score Calculator — PTE to IELTS & PR Points" />
        <meta
          property="og:description"
          content="Type your PTE Academic scores to see the IELTS band for every skill, your CEFR level and your Australian PR English points — built on the official Pearson alignment."
        />
        <meta property="og:url" content="https://www.languageacademy.com.au/pte-score-calculator/" />
        <meta property="og:image" content="https://www.languageacademy.com.au/assets/pte-score-calculator-og.png" />
        <meta property="og:locale" content="en_AU" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PTE Academic Score Calculator — PTE to IELTS & PR Points" />
        <meta
          name="twitter:description"
          content="See the IELTS band for every PTE skill, your CEFR level and your Australian PR points — instantly."
        />
        <meta name="twitter:image" content="https://www.languageacademy.com.au/assets/pte-score-calculator-og.png" />
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>

      {/* ================= HEADER ================= */}
      <Header />
      <div className="new-design">
      <div className="pte-score-academic-page js">
      {/* ================= HERO ================= */}
      <section className="hero">
        <div className="wrap">
          <span className="hero-badge"><i></i>PTE Academic · Official Pearson Alignment</span>
          <h1>PTE Academic <span className="accent">Score Calculator</span></h1>
          <p className="lede">
            <strong>Type the scores from your PTE score report</strong> — see the IELTS band for every module, your CEFR level and your Australian PR points, all in one place.
          </p>
          <div className="hero-trust">
            <span className="stars" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#A9D54B">
                  <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9z" />
                </svg>
              ))}
            </span>
            <span>Trusted by <b>50,000+ students</b></span>
          </div>
        </div>
      </section>

      {/* ================= CALCULATOR ================= */}
      <div className="wrap calc-zone" id="tool">
        <div className="report" aria-label="PTE Academic score calculator">
          <div className="r-head">
            <div>
              <h2>Your scores, decoded</h2>
              <div className="sub">Everything updates as you type — module by module, then your overall.</div>
            </div>
            <span className="r-badge">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 13l4 4L19 7" stroke="#557414" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Official Pearson PTE–IELTS alignment
            </span>
          </div>

          {/* band strip */}
          <div className="strip">
            <div className="strip-head">
              <span className="t">Your skills on the IELTS scale</span>
              <span className="l">L R S W + Overall · 4.5 – 9.0</span>
            </div>
            <div className="scale" id="scale" role="img" aria-label="Your PTE modules positioned on the IELTS band scale">
              <div className="s-track" />
              {SCALE_TICKS.map((t) => (
                <React.Fragment key={t.b}>
                  <div className={`s-tick${t.em ? " em" : ""}`} style={{ left: t.left }} />
                  <div className={`s-lab${t.em ? " em" : ""}${t.keep ? " keep" : ""}`} style={{ left: t.left }}>
                    {t.label}
                  </div>
                </React.Fragment>
              ))}
              {MARKER_ORDER.map(renderMarker)}
            </div>
          </div>

          {/* module + overall rows */}
          <div className="calc-rows" id="rows">
            {SK.map((s) => {
              const row = moduleRows[s];
              return (
                <div className={`calc-row${row.bad ? " bad" : ""}`} id={`row-${s}`} key={s}>
                  <div>
                    <div className="r-name">{s}</div>
                    <div className="r-hint">Enter 10–90</div>
                  </div>
                  <div className="r-in">
                    <input
                      type="number"
                      id={`in-${s}`}
                      min="10"
                      max="90"
                      step="1"
                      inputMode="numeric"
                      placeholder="–"
                      aria-label={`${s} PTE score`}
                      value={scores[s]}
                      ref={s === "Listening" ? listeningRef : undefined}
                      className={s === "Listening" && coachShown ? "pulse" : undefined}
                      onChange={(e) => handleChange(s, e.target.value)}
                      onFocus={() => dismissCoach(true)}
                    />
                    {s === "Listening" && coachMounted && (
                      <div className={`coach${coachShown ? " show" : ""}`} role="status">
                        Start here — type your scores straight from your <b>PTE score report</b>
                        <button type="button" aria-label="Dismiss tip" onClick={() => dismissCoach(true)}>
                          ×
                        </button>
                        <i className="c-arr" />
                      </div>
                    )}
                  </div>
                  <div className="r-out">
                    <span className="k">IELTS</span>
                    <span className={row.vClass} id={`v-${s}`}>{row.text}</span>
                  </div>
                </div>
              );
            })}

            <div className={`calc-row calc-row-overall${overallRow.bad ? " bad" : ""}`} id="row-Overall">
              <div>
                <div className="r-name">Overall score</div>
                <div className="r-note">As shown on your score report</div>
                <div className="r-hint">Enter 10–90</div>
              </div>
              <div className="r-in">
                <input
                  type="number"
                  id="in-Overall"
                  min="10"
                  max="90"
                  step="1"
                  inputMode="numeric"
                  placeholder="–"
                  aria-label="Overall PTE score"
                  value={scores.Overall}
                  onChange={(e) => handleChange("Overall", e.target.value)}
                  onFocus={() => dismissCoach(true)}
                />
              </div>
              <div className="r-out">
                <span className="k">IELTS · CEFR</span>
                <span className={overallRow.vClass} id="v-Overall">{overallRow.text}</span>
                <div className="chips" id="ov-chips">
                  {overallRow.cefr && <span className="chip green">CEFR {overallRow.cefr}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Australia PR */}
          <div className="prb" aria-live="polite">
            <div className="prb-top">
              <span className="t">🇦🇺 Australian PR — English points</span>
              <span className="s">
                Set by your <strong>lowest</strong> module — each band needs a different score per module
              </span>
            </div>
            <div className="meter" id="meter">
              {[
                { key: "competent", p: "0", n: "Competent" },
                { key: "proficient", p: "10", n: "Proficient" },
                { key: "superior", p: "20", n: "Superior" },
              ].map((seg) => (
                <div className={`seg${pr.ready && pr.segOn === seg.key ? " on" : ""}`} data-l={seg.key} key={seg.key}>
                  <div className="p">{seg.p}</div>
                  <div className="n">{seg.n}</div>
                </div>
              ))}
            </div>

            {!pr.ready && (
              <div className="pr-empty" id="pr-empty">
                Enter your four module scores to see your English level, your points, and exactly what you still need.
              </div>
            )}

            {pr.ready && (
              <div id="pr-out">
                {!pr.showWin && (
                  <p className="pr-msg" id="pr-msg" dangerouslySetInnerHTML={{ __html: pr.msgHtml }} />
                )}
                {!pr.showWin && (
                  <div className="needs" id="needs">
                    {pr.needs.map((nr) => (
                      <div className="nrow" key={nr.s}>
                        <span className="nm">{nr.s}</span>
                        {nr.ok ? (
                          <span className="vl ok tnum">{nr.have} — already there ✓</span>
                        ) : (
                          <span className="vl gap tnum">
                            {nr.have} → {nr.req}{"\u00A0\u00A0"}(+{nr.gap})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {pr.showWin && (
                  <div className="pr-win" id="pr-win">
                    Superior English — 20 points. The maximum English points for skilled migration. ✓
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="cfoot">
            <span className="acts">
              <button className="linkbtn" id="sample" type="button" onClick={handleSample}>
                Try sample scores
              </button>
              <button className="linkbtn" id="reset" type="button" onClick={handleReset}>
                Clear
              </button>
            </span>
            <span className="src">Data: official Pearson PTE–IELTS alignment · Home Affairs English bands</span>
          </div>
        </div>

        <div className="goodtoknow">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="#557414" strokeWidth="1.8" />
            <path d="M12 8v5M12 16h.01" stroke="#557414" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>
            <b>Good to know:</b> requirements and visa policies change from time to time. For your specific course or visa, always check the latest requirements with the official authority — your university or the Department of Home Affairs.
          </span>
        </div>
      </div>

      {/* ================= CHART ================= */}
      <section className="sec" id="chart">
        <div className="wrap narrow">
          <div className="sec-h reveal">
            <h2>PTE to IELTS <span className="accent on-light">Conversion Chart</span></h2>
            <p>The official alignment between PTE Academic and IELTS. Each skill converts on its own scale — that's why the calculator reads your modules separately.</p>
          </div>
          <div className="twrap reveal">
            <div className="tscroll">
              <table>
                <caption className="sr-only">PTE Academic to IELTS conversion chart — overall and per skill, official Pearson alignment</caption>
                <thead>
                  <tr>
                    <th scope="col">IELTS Band</th>
                    <th scope="col">PTE Overall</th>
                    <th scope="col">Listening</th>
                    <th scope="col">Reading</th>
                    <th scope="col">Speaking</th>
                    <th scope="col">Writing</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["9.0", "90", "81–90", "81–90", "—", "—"],
                    ["8.5", "86–89", "75–80", "75–80", "90", "90"],
                    ["8.0", "79–85", "69–74", "70–74", "88–89", "85–89"],
                    ["7.5", "71–78", "64–68", "65–69", "83–87", "77–84"],
                    ["7.0", "63–70", "58–63", "59–64", "76–82", "69–76"],
                    ["6.5", "55–62", "53–57", "54–58", "66–75", "60–68"],
                    ["6.0", "47–54", "47–52", "48–53", "54–65", "51–59"],
                    ["5.5", "39–46", "40–46", "42–47", "39–53", "41–50"],
                    ["5.0", "31–38", "33–39", "36–41", "24–38", "29–40"],
                    ["4.5", "24–30", "26–32", "29–35", "14–23", "17–28"],
                  ].map((r) => (
                    <tr key={r[0]}>
                      <td>{r[0]}</td>
                      <td className="key">{r[1]}</td>
                      <td>{r[2]}</td>
                      <td>{r[3]}</td>
                      <td>{r[4]}</td>
                      <td>{r[5]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="tnote reveal">
            Heading to Canada instead? Use the <a href="/pte-core-score-calculator/">PTE Core to CLB calculator</a>.
          </p>
        </div>
      </section>

      {/* ================= PR TABLE ================= */}
      <section className="sec" id="pr-table">
        <div className="wrap narrow">
          <div className="sec-h reveal">
            <h2>PR Points, <span className="accent on-light">Module by Module</span></h2>
            <p>Each PR English band equals an IELTS level in <strong>every</strong> skill — so the PTE score you need is different for each module. This chart is where most people get it wrong.</p>
          </div>
          <div className="twrap reveal">
            <div className="tscroll">
              <table>
                <caption className="sr-only">Minimum PTE Academic score per module for each Australian PR English band</caption>
                <thead>
                  <tr>
                    <th scope="col">English Level</th>
                    <th scope="col">Points</th>
                    <th scope="col">= IELTS Each</th>
                    <th scope="col">Listening</th>
                    <th scope="col">Reading</th>
                    <th scope="col">Speaking</th>
                    <th scope="col">Writing</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Superior", "20", "8.0", "69", "70", "88", "85"],
                    ["Proficient", "10", "7.0", "58", "59", "76", "69"],
                    ["Competent", "0", "6.0", "47", "48", "54", "51"],
                  ].map((r) => (
                    <tr key={r[0]}>
                      <td>{r[0]}</td>
                      <td className="key">{r[1]}</td>
                      <td>{r[2]}</td>
                      <td>{r[3]}</td>
                      <td>{r[4]}</td>
                      <td>{r[5]}</td>
                      <td>{r[6]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="tnote reveal">
            You must reach the level in all four modules to claim the points — note Speaking (88) and Writing (85) sit far above Listening (69) and Reading (70) for Superior.
          </p>
        </div>
      </section>

      {/* ================= CEFR ================= */}
      <section className="sec">
        <div className="wrap narrow">
          <div className="sec-h reveal">
            <h2>What Your Score <span className="accent on-light">Really Means</span></h2>
            <p>Pearson aligns overall PTE Academic scores to the CEFR — the international standard for describing English ability.</p>
          </div>
          <div className="twrap reveal">
            <div className="tscroll">
              <table>
                <caption className="sr-only">Overall PTE Academic score to CEFR level</caption>
                <thead>
                  <tr>
                    <th scope="col">CEFR</th>
                    <th scope="col">PTE Overall</th>
                    <th scope="col">In Plain Terms</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["C2", "85–90", "The highest level the test measures — precise, effortless English"],
                    ["C1", "76–84", "Fluent and spontaneous, ready for academic and professional life"],
                    ["B2", "59–75", "Confident with complex text — a common university target"],
                    ["B1", "43–58", "Handles most everyday situations comfortably"],
                    ["A2", "30–42", "Simple, routine communication"],
                    ["A1", "10–29", "Basic phrases and expressions"],
                  ].map((r) => (
                    <tr key={r[0]}>
                      <td>{r[0]}</td>
                      <td className="key">{r[1]}</td>
                      <td style={{ textAlign: "left" }}>{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="sec">
        <div className="wrap">
          <div className="stats reveal">
            <div className="stat">
              <div className="tile" style={{ background: "var(--tile-lav)" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3L2 8l10 5 10-5-10-5zM5 10.5V16c0 1.5 3 3.5 7 3.5s7-2 7-3.5v-5.5" stroke="#5B5BD6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="num tnum">50,000<span>+</span></div>
              <div className="lbl">Students' Enrolled</div>
            </div>
            <div className="stat">
              <div className="tile" style={{ background: "var(--tile-pink)" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M4 21V5a1 1 0 011-1h8a1 1 0 011 1v16M4 21h16M14 9h5a1 1 0 011 1v11M7.5 8h3M7.5 12h3M7.5 16h3" stroke="#D6566F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="num tnum">10<span>+</span></div>
              <div className="lbl">Learning Centres</div>
            </div>
            <div className="stat">
              <div className="tile" style={{ background: "var(--tile-mint)" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M3 17l5-5 4 4 8-8M15 8h5v5" stroke="#2F9B5C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="num tnum">95<span>%</span></div>
              <div className="lbl">Success Rate</div>
            </div>
            <div className="stat">
              <div className="tile" style={{ background: "var(--tile-peach)" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="9" r="5.5" stroke="#D98425" strokeWidth="1.8" />
                  <path d="M8.5 13.5L7 21l5-2.5L17 21l-1.5-7.5" stroke="#D98425" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="num tnum">8<span>+</span></div>
              <div className="lbl">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= AI PORTAL ================= */}
      <section className="sec">
        <div className="wrap">
          <div className="ai reveal">
            <h2>Know Your Real Score with <span className="accent">AI Practice &amp; Guidance</span></h2>
            <p className="sub">A calculator shows where you stand — Australia's most accurate AI practice platform shows you how to get to your target. Real exam questions, instant AI scoring, and a study plan built around your weakest module.</p>
            <div className="ai-feats">
              <div className="feat">
                <div className="fi">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.8" />
                  </svg>
                </div>
                <h4>Accurate Scoring</h4>
                <p>AI evaluation aligned with real exam standards — precise, unbiased results in minutes.</p>
              </div>
              <div className="feat">
                <div className="fi">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 19V10M10 19V5M16 19v-7M21 19H3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
                <h4>Detailed Performance Insights</h4>
                <p>See strengths, weaknesses and exactly which question types are costing you marks.</p>
              </div>
              <div className="feat">
                <div className="fi">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3l2.2 4.8L19 9l-4.8 2.2L12 16l-2.2-4.8L5 9l4.8-1.2L12 3zM18 15l1 2.5L21.5 18l-2.5 1L18 21.5 17 19l-2.5-1 2.5-.5 1-2.5z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
                  </svg>
                </div>
                <h4>Personalised Recommendations</h4>
                <p>Targeted practice strategies that lift your lowest module — where your points are decided.</p>
              </div>
            </div>
            <div className="btns">
              <a className="pill pill-green pill-lg" href="https://www.languageacademy.com.au/admin/test-material">Start Free PTE Practice <span className="arr">↗</span></a>
              <a className="pill pill-out pill-lg" href="https://www.languageacademy.com.au/admin/mock">Take a Free Mock Test <span className="arr">↗</span></a>
            </div>
          </div>
        </div>
      </section>

      </div>
      {/* /.pte-score-academic-page (main) */}

      {/* ================= FAQ (shared component) ================= */}
      <div className="p-5" id="faq">
        <FaqSection questions={faqQuestions} />
      </div>

      <div className="pte-score-academic-page js">
      {/* ================= SOURCES ================= */}
      <section className="sec last">
        <div className="wrap narrow">
          <div className="sec-h reveal">
            <h2>Built on <span className="accent on-light">Official Data</span></h2>
          </div>
          <div className="srcs reveal">
            <a className="src" href="https://www.pearsonpte.com/pte-academic/scoring/understand-your-score/" rel="noopener" target="_blank">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 7h10v10M7 17L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Pearson — Understand your PTE score
            </a>
            <a className="src" href="https://www.pearsonpte.com/" rel="noopener" target="_blank">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 7h10v10M7 17L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Pearson — PTE / IELTS alignment
            </a>
            <a className="src" href="https://immi.homeaffairs.gov.au/" rel="noopener" target="_blank">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 7h10v10M7 17L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Department of Home Affairs — English requirements
            </a>
          </div>
        </div>
      </section>
      </div>
      {/* /.pte-score-academic-page (sources) */}

      {/* ================= FOOTER ================= */}
      </div>
      <Footer />
    </>
  );
};

export default PteScoreAcademic;
